import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";
import api from "../services/api";

const AppSettingsContext = createContext({
  appName: "CarWash Pro",
  appIcon: null,
  sidebarColor: "",
  primaryColor: "",
  sidebarDisplay: "both",
  refresh: () => {},
});

export const AppSettingsProvider = ({ children }) => {
  const [appName, setAppName] = useState("CarWash Pro");
  const [appIcon, setAppIcon] = useState(null);
  const [sidebarColor, setSidebarColor] = useState("");
  const [primaryColor, setPrimaryColor] = useState("");
  const [sidebarDisplay, setSidebarDisplay] = useState("both");

  useEffect(() => {
    document.title = appName || "CarWash Pro";
  }, [appName]);

  const hexLuminance = (hex) => {
    const h = hex.replace("#", "");
    if (h.length !== 6) return 0;
    const [r, g, b] = [0, 2, 4].map((i) => {
      const c = parseInt(h.slice(i, i + 2), 16) / 255;
      return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const applyColors = useCallback((sidebar, primary) => {
    if (sidebar) {
      document.documentElement.style.setProperty("--sidebar-bg", sidebar);
      const isLight = hexLuminance(sidebar) > 0.4;
      document.documentElement.style.setProperty(
        "--sidebar-text",
        isLight ? "#334155" : "rgba(255,255,255,0.80)",
      );
      document.documentElement.style.setProperty(
        "--sidebar-label",
        isLight ? "rgba(0,0,0,0.40)" : "rgba(255,255,255,0.45)",
      );
      document.documentElement.style.setProperty(
        "--sidebar-active-text",
        isLight ? "#0f172a" : "#ffffff",
      );
      document.documentElement.style.setProperty(
        "--sidebar-active",
        isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.18)",
      );
    }
    if (primary) {
      document.documentElement.style.setProperty("--primary", primary);
      document.documentElement.style.setProperty("--primary-dark", primary);
    }
  }, []); // eslint-disable-line

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/settings/public");
      setAppName(res.data.app_name || "CarWash Pro");
      setAppIcon(res.data.app_icon || null);
      setSidebarColor(res.data.sidebar_color || "");
      setPrimaryColor(res.data.primary_color || "");
      setSidebarDisplay(res.data.sidebar_display || "both");
      applyColors(res.data.sidebar_color, res.data.primary_color);
    } catch (_) {
      // silently fail — use defaults
    }
  }, [applyColors]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AppSettingsContext.Provider
      value={{
        appName,
        appIcon,
        sidebarColor,
        primaryColor,
        sidebarDisplay,
        refresh,
      }}
    >
      {children}
    </AppSettingsContext.Provider>
  );
};

export const useAppSettings = () => useContext(AppSettingsContext);
